import React, {useEffect, useState} from 'react';
import {Cascader, Divider, message} from 'antd';
import type {CascaderProps, GetProp} from 'antd';
import {ProFormFieldItemProps} from '@ant-design/pro-form/es/typing';
import {requestUrl} from '@oakjs/antd-pro/dist/cjs/services/base/ServiceRequest';

interface IProps {
  idKey?: string;
  value?: string;
  params?: string;
  groupType?: string;
  onChange?: (value: string | number | null) => void;
}

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
  children?: Option[];
}

type DefaultOptionType = GetProp<CascaderProps, 'options'>[number];

const MaterialKinds: React.FC<IProps & ProFormFieldItemProps> = ({onChange, ...props}) => {
  const idKey = props.idKey || 'code';
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectValue, setSelectValue] = useState<Array<Array<string | number>>>([]);
  const [filterCodes, setFilterCodes] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const syncChange = (value: Array<Array<string | number>>, currentFilterCodes = filterCodes) => {
    if (value.length === 0) {
      setSelectValue([]);
      onChange?.('');
      setIsAllSelected(false);
    } else {
      const lastItems = value.map((subArray) => subArray[subArray.length - 1]);
      setSelectValue(value);
      onChange?.(lastItems.join(','));

      const isNowAllSelected = currentFilterCodes.length > 0
        && currentFilterCodes.every((code) => lastItems.includes(code));
      setIsAllSelected(isNowAllSelected);
    }
  };

  const handleSelectAll = async () => {
    if (filterCodes.length === 0) {
      return;
    }

    const currentlySelectedCodes = selectValue.map((path) => path[path.length - 1]);
    const isCurrentlyAllSelected = filterCodes.every((code) => currentlySelectedCodes.includes(code));

    if (isCurrentlyAllSelected) {
      setSelectValue([]);
      onChange?.('');
      setIsAllSelected(false);
    } else {
      const buildPathsForCodes = (
        nodes: Option[],
        targetCodes: string[],
        path: Array<string | number> = [],
      ): Array<Array<string | number>> => {
        let paths: Array<Array<string | number>> = [];
        nodes.forEach((node) => {
          const currentPath = [...path, node.value];
          if (targetCodes.includes(String(node.value))) {
            paths.push(currentPath);
          } else if (node.children && node.children.length > 0) {
            paths = paths.concat(buildPathsForCodes(node.children, targetCodes, currentPath));
          }
        });
        return paths;
      };

      const selectedPaths = buildPathsForCodes(options, filterCodes);
      setSelectValue(selectedPaths);
      onChange?.(filterCodes.join(','));
      setIsAllSelected(true);
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);

        if (
          props.params
          && props.params.includes('categoryCodes=undefined')
          && props.params.includes('ipCodes=undefined')
        ) {
          setOptions([]);
          setLoading(false);
          return;
        }

        const response = await requestUrl('/materialkinds/tree?parentCode=ROOT_NODE');

        if (!Array.isArray(response)) {
          message.error('数据格式错误');
          return;
        }

        let filteredResponse = response;
        const disabledCodes: string[] = [];
        let currentFilterCodes: string[] = [];

        if (props.params && !props.params.includes('parentCode=ROOT_NODE')) {
          const urlParams = new URLSearchParams(props.params);
          let combinedCategoryCodes: string[] = [];

          const categoryCodesStr = urlParams.get('categoryCodes');
          if (categoryCodesStr && categoryCodesStr !== 'undefined') {
            const cleanCategoryCodes = categoryCodesStr.trim().replace(/^,+|,+$/g, '');
            if (cleanCategoryCodes) {
              combinedCategoryCodes = [
                ...combinedCategoryCodes,
                ...cleanCategoryCodes.split(',').map((code) => code.trim()),
              ];
            }
          }

          const ipCodesStr = urlParams.get('ipCodes');
          if (ipCodesStr && ipCodesStr !== 'undefined') {
            const cleanIpCodes = ipCodesStr.trim().replace(/^,+|,+$/g, '');
            if (cleanIpCodes) {
              combinedCategoryCodes = [
                ...combinedCategoryCodes,
                ...cleanIpCodes.split(',').map((code) => code.trim()),
              ];
            }
          }

          const uniqueCodes = Array.from(new Set(combinedCategoryCodes)).filter((code) => code !== '');
          const newParams = `categoryCodes=${uniqueCodes.join(',')}`;

          try {
            const kindsCodesRes = await requestUrl(`/materialkinds/findKindsCode?${newParams}`);
            const kindsCodes: string[] = Array.isArray(kindsCodesRes) ? kindsCodesRes.map(String) : [];

            currentFilterCodes = kindsCodes;
            filteredResponse = response.filter((item) => kindsCodes.includes(item[idKey]));
          } catch (error) {
            console.error('获取种类编码失败', error);
          }
        } else if (props.params && props.params.includes('parentCode=ROOT_NODE')) {
          const urlParams = new URLSearchParams(props.params);
          const categoryCodes = urlParams.get('categoryCodes');

          if (categoryCodes && categoryCodes !== 'undefined' && categoryCodes !== 'null' && categoryCodes.trim() !== '') {
            const kindsCodesRes = await requestUrl(`/materialkinds/findKindsCode?categoryCodes=${categoryCodes}`);
            const labelKindsCodes: string[] = Array.isArray(kindsCodesRes) ? kindsCodesRes.map(String) : [];

            currentFilterCodes = labelKindsCodes;
            filteredResponse = response.filter((item) => labelKindsCodes.includes(item[idKey]));
          }
        }

        const data = filteredResponse.map((item) => {
          const isDisabled = disabledCodes.includes(item[idKey]);
          return {
            value: item[idKey],
            label: item.name,
            disabled: isDisabled,
            children: item.children?.map((child: {id: unknown; code: string; name: string; children?: any[]}) => {
              const isChildDisabled = disabledCodes.includes(child.code);
              return {
                value: child.code,
                label: child.name,
                disabled: isChildDisabled,
                children: child.children?.map((grandchild: {id: unknown; code: string; name: string; children?: any[]}) => {
                  const isGrandchildDisabled = disabledCodes.includes(grandchild.code);
                  return {
                    value: grandchild.code,
                    label: grandchild.name,
                    disabled: isGrandchildDisabled,
                    children: grandchild.children?.map((
                      greatGrandchild: {id: unknown; code: string; name: string; children?: any[]},
                    ) => {
                      const isGreatGrandchildDisabled = disabledCodes.includes(greatGrandchild.code);
                      return {
                        value: greatGrandchild.code,
                        label: greatGrandchild.name,
                        disabled: isGreatGrandchildDisabled,
                      };
                    }),
                  };
                }),
              };
            }),
          };
        });
        setOptions(data);
        setFilterCodes(currentFilterCodes);

        if (props.value) {
          const values = typeof props.value === 'string' ? props.value.split(',') : [];

          const validValues = values.filter((value) => {
            const existsInTree = (node: any): boolean => {
              if (!node) {
                return false;
              }
              if (node.code === value) {
                return true;
              }
              if (node.children && node.children.length > 0) {
                return node.children.some((child) => existsInTree(child));
              }
              return false;
            };

            return filteredResponse.some((node) => existsInTree(node));
          });

          if (validValues.length > 0) {
            const result: Array<Array<string | number>> = [];

            const findParentCodes = (
              node: any,
              targetValue: string,
              path: Array<string | number> = [],
            ): void => {
              if (!node) {
                return;
              }
              if (node.code === targetValue) {
                path.push(node.code);
                result.push([...path]);
                path.pop();
                return;
              }
              if (node.children && node.children.length > 0) {
                path.push(node.code);
                node.children.forEach((child) => findParentCodes(child, targetValue, path));
                path.pop();
              }
            };

            validValues.forEach((value) => {
              response.forEach((rootNode) => {
                findParentCodes(rootNode, value);
              });
            });
            setSelectValue(result);
            syncChange(result, currentFilterCodes);
          } else {
            setSelectValue([]);
            syncChange([], currentFilterCodes);
          }
        }
      } catch (error) {
        message.error('加载数据失败');
        console.error('Failed to fetch options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [props.value, props.params]);

  const onCascaderChange: CascaderProps<Option, 'value', true>['onChange'] = (value) => {
    syncChange(value);
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) => (
    path.some((option) => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1)
  );

  const popupRender = (menus: React.ReactNode) => (
    <div>
      {menus}
      <Divider style={{margin: 0}} />
      {filterCodes.length > 0 && (
        <a
          onClick={handleSelectAll}
          style={{marginLeft: 8, whiteSpace: 'nowrap', color: isAllSelected ? 'blue' : ''}}
        >
          全选
        </a>
      )}
    </div>
  );

  return (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <Cascader
        allowClear
        changeOnSelect
        loading={loading}
        multiple
        onChange={onCascaderChange}
        options={options}
        popupRender={popupRender}
        showCheckedStrategy={Cascader.SHOW_PARENT}
        showSearch={{filter}}
        style={{width: '100%'}}
        value={selectValue}
      />
    </div>
  );
};

export default MaterialKinds;
